import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { useUser } from '../../hooks/useUser'
import ReportCreator from './ReportCreator'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import MapLibrePreviewMap from './MapLibrePreviewMap'

interface ReportRow {
  id: string
  client_id: number
  client_name: string
  date: string               // data przygotowania raportu (z backendu)
  category?: 'water' | 'sewer' | null
  readonly: boolean          // zatwierdzony = true
  is_finished?: boolean      // czy prace zakończone
  author_name?: string       // imię i nazwisko
  has_media?: boolean        // ma multimedia
  location?: string          // miejscowość
  street_address?: string    // ulica/nr działki
}

interface ReportDetail extends ReportRow {
  // Ślad pochodzenia — wypełniony tylko dla raportów wygenerowanych ze zlecenia.
  service_id?: string | null
  ordered_by_name?: string | null
  planned_by_name?: string | null
  completed_by_name?: string | null
  work_type?: 'konserwacja' | 'awaria'
  reported_by?: 'system' | 'osoba' | null
  reporter_name?: string | null
  reporter_phone?: string | null
  description?: string | null
  materials?: string | null
  recommendations?: string | null
  latitude?: string | null
  longitude?: string | null
  infrastructure_object?: {
    id: number
    name: string
    address?: string
    location?: string
    street_address?: string
  } | null
  images?: Array<{
    id: number
    filename: string
    original_filename: string
    file_path: string
    file_size: number
    mime_type: string
    uploaded_at?: string | null
  }>
}

interface Client { id: number; name: string }

interface ReportsListProps { token: string }

export default function ReportsList({ token }: ReportsListProps) {
  const [reports, setReports] = useState<ReportRow[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreator, setShowCreator] = useState(false)
  const [editingReportId, setEditingReportId] = useState<string | undefined>(undefined)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewReport, setPreviewReport] = useState<ReportDetail | null>(null)
  const [assigningCategory, setAssigningCategory] = useState(false)
  const [categoryToAssign, setCategoryToAssign] = useState<'water' | 'sewer' | ''>('')
  const [mediaModalOpen, setMediaModalOpen] = useState(false)
  const [mediaModalReport, setMediaModalReport] = useState<ReportDetail | null>(null)
  const [mediaModalLoading, setMediaModalLoading] = useState(false)

  // Hook do zarządzania użytkownikiem i uprawnieniami
  const { user, canDeleteReport, canApproveReports } = useUser(token)

  // Obsługa gestów dotykowych dla mobilnych
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  // Funkcje obsługi gestów dotykowych
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe || isRightSwipe) {
      // Znajdź element przewijalny i przesuń
      const scrollableElement = document.querySelector('.overflow-x-auto')
      if (scrollableElement) {
        const currentScroll = scrollableElement.scrollLeft
        const scrollAmount = 200 // piksele do przewinięcia

        if (isLeftSwipe) {
          scrollableElement.scrollLeft = currentScroll + scrollAmount
        } else {
          scrollableElement.scrollLeft = currentScroll - scrollAmount
        }
      }
    }
  }

  const { showSuccess, showError, showConfirm, showInfo } = useNotificationContext()

  // Filtry - wartości w polach input (do wprowadzania)
  const [clientFilterInput, setClientFilterInput] = useState<number | ''>('')
  const [dateFromFilterInput, setDateFromFilterInput] = useState('')
  const [dateToFilterInput, setDateToFilterInput] = useState('')
  const [statusFilterInput, setStatusFilterInput] = useState<string>('')
  const [searchFilterInput, setSearchFilterInput] = useState('')
  const [locationFilterInput, setLocationFilterInput] = useState('')
  const [categoryFilterInput, setCategoryFilterInput] = useState('')

  // Filtry - aktywne wartości (używane do filtrowania)
  const [clientFilter, setClientFilter] = useState<number | ''>('')
  const [dateFromFilter, setDateFromFilter] = useState('')
  const [dateToFilter, setDateToFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [searchFilter, setSearchFilter] = useState('')
  const [locationFilter, setLocationFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')

  // Sortowanie
  const [sortBy, setSortBy] = useState('date') // 'date' | 'id' | 'client_name' | 'author_name' | 'readonly'
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC')

  useEffect(() => {
    loadClients()
    loadReports()
  }, [])

  useEffect(() => {
    loadReports()
  }, [clientFilter, dateFromFilter, dateToFilter, statusFilter, searchFilter, locationFilter, categoryFilter, sortBy, sortOrder])

  // Filtry tekstowe stosują się automatycznie z opóźnieniem (debounce),
  // aby nie strzelać zapytaniem po każdej literze.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchFilter(searchFilterInput)
      setLocationFilter(locationFilterInput)
    }, 400)
    return () => clearTimeout(t)
  }, [searchFilterInput, locationFilterInput])

  // Zwijane filtry na mobile + licznik aktywnych filtrów
  const [filtersOpen, setFiltersOpen] = useState(false)
  const activeFilterCount = [
    clientFilter, dateFromFilter, dateToFilter, statusFilter,
    searchFilter, locationFilter, categoryFilter
  ].filter(v => v !== '' && v !== null).length

  const clearInputFilters = () => {
    setClientFilterInput('')
    setDateFromFilterInput('')
    setDateToFilterInput('')
    setStatusFilterInput('')
    setSearchFilterInput('')
    setLocationFilterInput('')
    setCategoryFilterInput('')
  }

  const clearActiveFilters = () => {
    setClientFilter('')
    setDateFromFilter('')
    setDateToFilter('')
    setStatusFilter('')
    setSearchFilter('')
    setLocationFilter('')
    setCategoryFilter('')
  }

  const clearFilters = () => {
    clearInputFilters()
    clearActiveFilters()
  }

  const loadClients = async () => {
    try {
      const response = await fetch('/api/clients/list', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setClients(data)
      }
    } catch (err) {
      console.error('Error loading clients:', err)
    }
  }

  const loadReports = async () => {
    try {
      setLoading(true)
      setError('')
      const params = new URLSearchParams()
      if (clientFilter) params.append('client_id', clientFilter.toString())
      if (dateFromFilter) params.append('date_from', dateFromFilter)
      if (dateToFilter) params.append('date_to', dateToFilter)
      if (statusFilter) params.append('status', statusFilter)
      if (searchFilter) params.append('client_name_search', searchFilter)
      if (locationFilter) params.append('location_filter', locationFilter)
      if (categoryFilter) params.append('category', categoryFilter)
      params.append('sort_by', sortBy)     // 'date' i 'client_name' sortuje backend
      params.append('sort_order', sortOrder)

      const response = await fetch(`/api/reports?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })

      if (!response.ok) {
        setError('Błąd podczas ładowania raportów')
        setReports([])
        return
      }

      const list: ReportRow[] = await response.json()
      const hydrated = list.map(r => ({
        ...r,
        author_name: r.author_name ?? '(brak danych)',
        has_media: typeof r.has_media === 'boolean' ? r.has_media : false,
        location: r.location ?? '',
        street_address: r.street_address ?? '',
        category: r.category ?? null,
      }))

      const clientSortable = new Set(['id', 'author_name', 'is_finished', 'readonly'])
      let finalList = hydrated
      if (clientSortable.has(sortBy)) {
        finalList = [...hydrated].sort((a, b) => {
          const dir = sortOrder === 'ASC' ? 1 : -1
          const av = (a as any)[sortBy]
          const bv = (b as any)[sortBy]
          if (av === bv) return 0
          if (av === undefined || av === null) return 1
          if (bv === undefined || bv === null) return -1
          return (av > bv ? 1 : -1) * dir
        })
      }

      setReports(finalList)
    } catch (err) {
      setError('Błąd połączenia z serwerem')
      setReports([])
    } finally {
      setLoading(false)
    }
  }

  const openReportCreator = () => setShowCreator(true)
  const handleReportCreated = () => loadReports()

  const deleteReport = async (reportId: string) => {
    const confirmed = await showConfirm('Potwierdzenie usunięcia', `Czy na pewno chcesz usunąć raport ${reportId}?`)
    if (!confirmed) return
    try {
      const response = await fetch(`/api/reports/${reportId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        showSuccess('Raport został usunięty pomyślnie')
        loadReports()
      } else {
        const errorData = await response.json()
        let msg = 'Błąd podczas usuwania raportu'
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            msg = errorData.detail.map((err: any) => {
              if (typeof err === 'string') return err
              return err.msg || err.message || 'Błąd walidacji'
            }).join('; ')
          } else if (typeof errorData.detail === 'string') {
            msg = errorData.detail
          } else if (typeof errorData.detail === 'object' && errorData.detail.msg) {
            msg = errorData.detail.msg
          }
        }
        showError('Błąd usuwania', msg)
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  const markReadonly = async (reportId: string) => {
    const confirmed = await showConfirm('Potwierdzenie zatwierdzenia', `Czy na pewno chcesz oznaczyć raport ${reportId} jako zatwierdzony? Tej operacji nie można cofnąć.`)
    if (!confirmed) return
    try {
      const response = await fetch(`/api/reports/${reportId}/readonly`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        showSuccess('Raport został zatwierdzony pomyślnie')
        loadReports()
      } else {
        const errorData = await response.json()
        let msg = 'Błąd podczas zatwierdzania raportu'
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            msg = errorData.detail.map((err: any) => {
              if (typeof err === 'string') return err
              return err.msg || err.message || 'Błąd walidacji'
            }).join('; ')
          } else if (typeof errorData.detail === 'string') {
            msg = errorData.detail
          } else if (typeof errorData.detail === 'object' && errorData.detail.msg) {
            msg = errorData.detail.msg
          }
        }
        showError('Błąd zatwierdzania', msg)
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }



  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSortBy(field)
      setSortOrder('DESC')
    }
  }

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return '↕️'
    return sortOrder === 'ASC' ? '↑' : '↓'
  }

  // ----- PODGLĄD -----
  const openPreview = async (reportId: string) => {
    setPreviewOpen(true)
    setPreviewLoading(true)
    setPreviewReport(null)
    setAssigningCategory(false)
    setCategoryToAssign('')
    try {
      const resp = await fetch(`/api/reports/${reportId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!resp.ok) throw new Error()
      const data: ReportDetail = await resp.json()
      setPreviewReport(data)
      setCategoryToAssign(data.category ?? '')
    } catch {
      showError('Błąd', 'Nie udało się pobrać danych raportu do podglądu.')
      setPreviewOpen(false)
    } finally {
      setPreviewLoading(false)
    }
  }

  const closePreview = () => {
    setPreviewOpen(false)
    setPreviewReport(null)
    setPreviewLoading(false)
    setAssigningCategory(false)
    setCategoryToAssign('')
  }

  const assignCategory = async () => {
    if (!previewReport) return
    if (categoryToAssign !== 'water' && categoryToAssign !== 'sewer') {
      showError('Błąd', 'Wybierz kategorię do przypisania.')
      return
    }
    setAssigningCategory(true)
    try {
      const resp = await fetch(`/api/reports/${previewReport.id}/category`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: categoryToAssign,
        })
      })

      if (!resp.ok) {
        let msg = 'Nie udało się przypisać kategorii.'
        try {
          const d = await resp.json()
          if (d.detail) {
            if (Array.isArray(d.detail)) {
              msg = d.detail.map((err: any) => {
                if (typeof err === 'string') return err
                return err.msg || err.message || 'Błąd walidacji'
              }).join('; ')
            } else if (typeof d.detail === 'string') {
              msg = d.detail
            } else if (typeof d.detail === 'object' && d.detail.msg) {
              msg = d.detail.msg
            }
          }
        } catch {}
        showError('Błąd', msg)
        return
      }

      setPreviewReport(prev => prev ? { ...prev, category: categoryToAssign } : prev)
      loadReports()
      showSuccess('Zapisano', 'Kategoria raportu została przypisana.')
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem.')
    } finally {
      setAssigningCategory(false)
    }
  }

  // ----- MODAL MULTIMEDIÓW -----
  const openMediaModal = async (reportId: string) => {
    setMediaModalOpen(true)
    setMediaModalLoading(true)
    setMediaModalReport(null)
    try {
      const resp = await fetch(`/api/reports/${reportId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!resp.ok) throw new Error()
      const data: ReportDetail = await resp.json()
      setMediaModalReport(data)
    } catch {
      showError('Błąd', 'Nie udało się pobrać multimediów z raportu.')
      setMediaModalOpen(false)
    } finally {
      setMediaModalLoading(false)
    }
  }

  const closeMediaModal = () => {
    setMediaModalOpen(false)
    setMediaModalReport(null)
    setMediaModalLoading(false)
  }

  // ----- GENEROWANIE PDF -----
  const generateAndDownloadPDF = async (reportId: string) => {
    try {
      showInfo('Generowanie PDF...', 'Trwa generowanie pliku PDF. Proszę czekać.')

      // Generuj PDF
      const response = await fetch('/api/pdf/generate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          report_id: reportId
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        let msg = 'Błąd podczas generowania PDF'
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            msg = errorData.detail.map((err: any) => {
              if (typeof err === 'string') return err
              return err.msg || err.message || 'Błąd walidacji'
            }).join('; ')
          } else if (typeof errorData.detail === 'string') {
            msg = errorData.detail
          } else if (typeof errorData.detail === 'object' && errorData.detail.msg) {
            msg = errorData.detail.msg
          }
        }
        throw new Error(msg)
      }

      const result = await response.json()
      const filename = result.filename

      // Pobierz plik
      const downloadResponse = await fetch(`/api/pdf/download/${filename}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!downloadResponse.ok) {
        throw new Error('Błąd podczas pobierania pliku')
      }

      // Utwórz link do pobrania
      const blob = await downloadResponse.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showSuccess('PDF wygenerowany', 'Plik PDF został pobrany pomyślnie')
    } catch (error) {
      console.error('Error generating PDF:', error)
      showError('Błąd generowania PDF', error instanceof Error ? error.message : 'Nieznany błąd')
    }
  }



  // Funkcja cofnięcia zatwierdzenia
  const undoReportApproval = async (reportId: string) => {
    const confirmed = await showConfirm(
      'Potwierdzenie cofnięcia zatwierdzenia',
      `Czy na pewno chcesz cofnąć zatwierdzenie raportu ${reportId}? Raport będzie ponownie edytowalny.`
    )
    if (!confirmed) return

    try {
      const response = await fetch(`/api/reports/${reportId}/undo-approval`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        showSuccess('Zatwierdzenie cofnięte', 'Raport jest ponownie edytowalny.')
        loadReports()
      } else {
        const errorData = await response.json()
        let msg = 'Błąd podczas cofania zatwierdzenia'
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            msg = errorData.detail.map((err: any) => {
              if (typeof err === 'string') return err
              return err.msg || err.message || 'Błąd walidacji'
            }).join('; ')
          } else if (typeof errorData.detail === 'string') {
            msg = errorData.detail
          } else if (typeof errorData.detail === 'object' && errorData.detail.msg) {
            msg = errorData.detail.msg
          }
        }
        showError('Błąd cofania zatwierdzenia', msg)
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  if (loading && reports.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Ładowanie raportów...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div className="flex items-center gap-3">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Raporty Serwisowe</h2>
          {loading && reports.length > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-full px-2.5 py-1">
              <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></span>
              Odświeżanie…
            </span>
          )}
        </div>
        <button
          onClick={openReportCreator}
          className="w-full sm:w-auto min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
        >
          + Nowy Raport
        </button>
      </div>

      {/* Filtry */}
      <div className="bg-white p-4 sm:p-6 rounded-xl shadow">
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="w-full flex items-center justify-between lg:pointer-events-none"
          aria-expanded={filtersOpen}
        >
          <span className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-gray-900">Filtry</h3>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold">
                {activeFilterCount}
              </span>
            )}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform lg:hidden ${filtersOpen ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div className={`${filtersOpen ? 'grid' : 'hidden'} lg:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4`}>
          {/* Klient */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Klient</label>
            <select
              value={clientFilterInput}
              onChange={(e) => { const v = e.target.value === '' ? '' : parseInt(e.target.value); setClientFilterInput(v); setClientFilter(v) }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszyscy klienci</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          {/* Data od */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data od</label>
            <input
              type="date"
              value={dateFromFilterInput}
              onChange={(e) => { setDateFromFilterInput(e.target.value); setDateFromFilter(e.target.value) }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Data do */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data do</label>
            <input
              type="date"
              value={dateToFilterInput}
              onChange={(e) => { setDateToFilterInput(e.target.value); setDateToFilter(e.target.value) }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilterInput}
              onChange={(e) => { setStatusFilterInput(e.target.value); setStatusFilter(e.target.value) }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszystkie</option>
              <option value="editable">Edytowalne</option>
              <option value="approved">Zatwierdzone</option>
            </select>
          </div>

          {/* Szukaj klienta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Szukaj klienta</label>
            <input
              type="text"
              value={searchFilterInput}
              onChange={(e) => setSearchFilterInput(e.target.value)}
              placeholder="Nazwa klienta..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Filtr miejscowość */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Miejscowość</label>
            <input
              type="text"
              value={locationFilterInput}
              onChange={(e) => setLocationFilterInput(e.target.value)}
              placeholder="Filtruj po miejscowości..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Kategoria */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kategoria</label>
            <select
              value={categoryFilterInput}
              onChange={(e) => { setCategoryFilterInput(e.target.value); setCategoryFilter(e.target.value) }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszystkie</option>
              <option value="water">Wodociągi</option>
              <option value="sewer">Kanalizacja</option>
            </select>
          </div>

          {/* Przyciski */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              disabled={activeFilterCount === 0 && !searchFilterInput && !locationFilterInput}
              className="w-full sm:w-auto min-h-[44px] px-4 py-2 rounded-lg font-medium text-sm border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ✕ Wyczyść filtry
            </button>
          </div>
        </div>
      </div>

      {/* Błąd */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Lista raportów - desktop: tabela, mobile: karty */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* === WIDOK DESKTOP (≥ lg) === */}
        <div
          className="hidden lg:block overflow-x-auto"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                  onClick={() => handleSort('date')}
                >
                  Data {getSortIcon('date')}
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                  onClick={() => handleSort('id')}
                >
                  Numer {getSortIcon('id')}
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                  onClick={() => handleSort('author_name')}
                >
                  Autor {getSortIcon('author_name')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Kategoria
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Miejscowość
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Adres
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Media
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Akcje
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {reports.map((report) => (
                <tr key={report.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {format(new Date(report.date), 'dd.MM.yyyy', { locale: pl })}
                  </td>
                  <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${
                    report.readonly ? 'text-green-600' : 'text-blue-500'
                  }`}>
                    <button
                      onClick={() => openPreview(report.id)}
                      className={`inline-block px-2 py-1 rounded border-2 hover:bg-gray-100 transition-colors ${
                        report.is_finished ? 'border-green-500' : 'border-blue-500'
                      }`}
                      title="Kliknij aby zobaczyć podgląd raportu"
                    >
                      {report.id}
                    </button>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {report.author_name ?? '(brak danych)'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {report.category === 'water'
                      ? 'Wodociągi'
                      : report.category === 'sewer'
                        ? 'Kanalizacja'
                        : '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {report.location || '-'}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900">
                    <div className="max-w-xs truncate" title={report.street_address || ''}>
                      {report.street_address || '-'}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {report.has_media ? (
                      <button
                        onClick={() => openMediaModal(report.id)}
                        title="Zobacz multimedia"
                        aria-label="Zobacz multimedia"
                        className="hover:text-blue-600 transition-colors"
                      >
                        📷
                      </button>
                    ) : (
                      <span title="Brak multimediów" aria-label="Brak multimediów" className="opacity-40">📷</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right text-sm font-medium">
                    <div className="inline-flex flex-wrap justify-end gap-x-3 gap-y-1">
                      <button
                        onClick={() => generateAndDownloadPDF(report.id)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Generuj PDF"
                      >
                        PDF
                      </button>
                      <button
                        onClick={() => {
                          setEditingReportId(report.id)
                          setShowCreator(true)
                        }}
                        disabled={report.readonly}
                        className={`${report.readonly ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-900'}`}
                      >
                        Edytuj
                      </button>

                      {!report.readonly && (
                        <>
                          {canApproveReports() && (
                            <button
                              onClick={() => markReadonly(report.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Zatwierdź
                            </button>
                          )}
                          {canDeleteReport() && (
                            <button
                              onClick={() => deleteReport(report.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Usuń
                            </button>
                          )}
                        </>
                      )}

                      {report.readonly && user?.role === 'ADMIN' && (
                        <button
                          onClick={() => undoReportApproval(report.id)}
                          className="text-orange-600 hover:text-orange-900"
                          title="Cofnij zatwierdzenie (tylko administrator)"
                        >
                          Cofnij zatwierdzenie
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* === WIDOK MOBILE / TABLET (< lg): karty === */}
        <div className="lg:hidden">
          {/* Pasek sortowania */}
          <div className="flex items-center flex-wrap gap-x-2 gap-y-1 px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600">
            <span className="font-medium">Sortuj:</span>
            <button
              onClick={() => handleSort('date')}
              className="px-2 py-1 rounded hover:bg-gray-200"
            >
              Data {getSortIcon('date')}
            </button>
            <button
              onClick={() => handleSort('id')}
              className="px-2 py-1 rounded hover:bg-gray-200"
            >
              Numer {getSortIcon('id')}
            </button>
            <button
              onClick={() => handleSort('author_name')}
              className="px-2 py-1 rounded hover:bg-gray-200"
            >
              Autor {getSortIcon('author_name')}
            </button>
          </div>

          <ul className="divide-y divide-gray-200">
            {reports.map((report) => (
              <li key={report.id} className="p-3 sm:p-4 hover:bg-gray-50">
                {/* Nagłówek karty: numer + data + status + media.
                    Lewa część zawija się (flex-wrap) na wąskich ekranach -
                    status łamie się do nowej linii zamiast nachodzić na ikonę zdjęć. */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0 flex items-center flex-wrap gap-x-2 gap-y-1">
                    <button
                      onClick={() => openPreview(report.id)}
                      className={`max-w-full truncate inline-flex items-center px-2 py-1 rounded border-2 text-sm font-semibold transition-colors hover:bg-gray-100 ${
                        report.is_finished ? 'border-green-500 text-green-700' : 'border-blue-500 text-blue-700'
                      }`}
                      title="Kliknij aby zobaczyć podgląd raportu"
                    >
                      #{report.id}
                    </button>
                    <span className="text-sm text-gray-700 whitespace-nowrap">
                      {format(new Date(report.date), 'dd.MM.yyyy', { locale: pl })}
                    </span>
                    {report.readonly && (
                      <span className="inline-block px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full whitespace-nowrap">
                        Zatwierdzony
                      </span>
                    )}
                  </div>

                  {report.has_media ? (
                    <button
                      onClick={() => openMediaModal(report.id)}
                      title="Zobacz multimedia"
                      aria-label="Zobacz multimedia"
                      className="shrink-0 min-w-[44px] min-h-[44px] -my-1.5 -mr-1.5 flex items-center justify-center text-lg rounded-xl hover:bg-blue-50 hover:text-blue-600 active:bg-blue-100 transition-colors"
                    >
                      📷
                    </button>
                  ) : (
                    <span
                      title="Brak multimediów"
                      className="shrink-0 min-w-[44px] min-h-[44px] -my-1.5 -mr-1.5 flex items-center justify-center text-lg opacity-30"
                    >
                      📷
                    </span>
                  )}
                </div>

                {/* Dane raportu */}
                <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm">
                  <dt className="text-gray-500">Autor:</dt>
                  <dd className="text-gray-900 break-words">{report.author_name ?? '(brak danych)'}</dd>

                  <dt className="text-gray-500">Kategoria:</dt>
                  <dd className="text-gray-900">
                    {report.category === 'water'
                      ? 'Wodociągi'
                      : report.category === 'sewer'
                        ? 'Kanalizacja'
                        : '-'}
                  </dd>

                  <dt className="text-gray-500">Miejscowość:</dt>
                  <dd className="text-gray-900 break-words">{report.location || '-'}</dd>

                  <dt className="text-gray-500">Adres:</dt>
                  <dd className="text-gray-900 break-words">{report.street_address || '-'}</dd>
                </dl>

                {/* Akcje */}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={() => openPreview(report.id)}
                    className="px-3 py-2 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  >
                    Podgląd
                  </button>
                  <button
                    onClick={() => generateAndDownloadPDF(report.id)}
                    className="px-3 py-2 text-xs font-medium rounded border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100"
                  >
                    PDF
                  </button>
                  <button
                    onClick={() => {
                      setEditingReportId(report.id)
                      setShowCreator(true)
                    }}
                    disabled={report.readonly}
                    className={`px-3 py-2 text-xs font-medium rounded border ${
                      report.readonly
                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                        : 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    Edytuj
                  </button>

                  {!report.readonly && canApproveReports() && (
                    <button
                      onClick={() => markReadonly(report.id)}
                      className="px-3 py-2 text-xs font-medium rounded border border-green-300 bg-green-50 text-green-700 hover:bg-green-100"
                    >
                      Zatwierdź
                    </button>
                  )}
                  {!report.readonly && canDeleteReport() && (
                    <button
                      onClick={() => deleteReport(report.id)}
                      className="px-3 py-2 text-xs font-medium rounded border border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                    >
                      Usuń
                    </button>
                  )}
                  {report.readonly && user?.role === 'ADMIN' && (
                    <button
                      onClick={() => undoReportApproval(report.id)}
                      className="px-3 py-2 text-xs font-medium rounded border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100"
                      title="Cofnij zatwierdzenie (tylko administrator)"
                    >
                      Cofnij zatwierdzenie
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {reports.length === 0 && !loading && (
          <div className="text-center py-12 px-4">
            <div className="text-5xl mb-3">📋</div>
            <p className="text-gray-900 font-medium mb-1">
              {activeFilterCount > 0 ? 'Brak raportów dla wybranych filtrów' : 'Nie ma jeszcze żadnych raportów'}
            </p>
            <p className="text-sm text-gray-500 mb-5">
              {activeFilterCount > 0
                ? 'Zmień kryteria wyszukiwania albo wyczyść filtry, aby zobaczyć wszystkie raporty.'
                : 'Utwórz pierwszy raport serwisowy, aby pojawił się na tej liście.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              {activeFilterCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="min-h-[44px] px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  ✕ Wyczyść filtry
                </button>
              )}
              <button
                onClick={openReportCreator}
                className="min-h-[44px] px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 transition-colors"
              >
                + Nowy Raport
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Podsumowanie */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex justify-between items-center text-sm text-blue-800">
          <span>Znaleziono raportów: <strong>{reports.length}</strong></span>
        </div>
      </div>

      {/* Modal kreatora/edycji */}
      <ReportCreator
        token={token}
        isOpen={showCreator}
        onClose={() => {
          setShowCreator(false)
          setEditingReportId(undefined)
        }}
        onReportCreated={handleReportCreated}
        reportId={editingReportId}
      />

      {/* Modal PODGLĄDU „jak PDF” */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="bg-white shadow-2xl w-full sm:max-w-5xl flex flex-col max-h-full sm:max-h-[90vh] sm:rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b shrink-0">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Podgląd raportu</h3>
              <button onClick={closePreview} className="p-1 -mr-1 text-gray-400 hover:text-gray-600" aria-label="Zamknij">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
              {previewLoading && (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Ładowanie…</span>
                </div>
              )}

              {!previewLoading && previewReport && (
                <div className="bg-white mx-auto shadow print:shadow-none">
                  {/* „Karta A4” */}
                  <div className="mx-auto bg-white border border-gray-200 rounded-md p-4 sm:p-8 w-full max-w-[900px]">
                    {/* Nagłówek */}
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900">Raport serwisowy</h1>
                        <p className="text-sm text-gray-600">Nr: <span className="font-medium text-gray-900">{previewReport.id}</span></p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Data raportu</p>
                        <p className="text-base font-medium text-gray-900">
                          {format(new Date(previewReport.date), 'dd.MM.yyyy', { locale: pl })}
                        </p>
                      </div>
                    </div>

                    {/* Dane podstawowe */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Klient</div>
                        <div className="text-base font-medium text-gray-900">{previewReport.client_name}</div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Autor</div>
                        <div className="text-base font-medium text-gray-900">{previewReport.author_name || '(brak danych)'}</div>
                      </div>
                      {previewReport.service_id && (
                        <>
                          <div className="space-y-1">
                            <div className="text-sm text-gray-500">Ze zlecenia</div>
                            <div className="text-base font-medium text-gray-900">{previewReport.service_id}</div>
                          </div>
                          <div className="space-y-1">
                            <div className="text-sm text-gray-500">Zlecił</div>
                            <div className="text-base font-medium text-gray-900">{previewReport.ordered_by_name || '—'}</div>
                          </div>
                          <div className="space-y-1">
                            <div className="text-sm text-gray-500">Zaplanował</div>
                            <div className="text-base font-medium text-gray-900">{previewReport.planned_by_name || '—'}</div>
                          </div>
                          <div className="space-y-1">
                            <div className="text-sm text-gray-500">Zrealizował</div>
                            <div className="text-base font-medium text-gray-900">{previewReport.completed_by_name || '—'}</div>
                          </div>
                        </>
                      )}
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Rodzaj pracy</div>
                        <div className="text-base font-medium text-gray-900 capitalize">
                          {previewReport.work_type === 'awaria' ? 'Awaria' : 'Konserwacja/planowana'}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Kategoria</div>
                        <div className="text-base font-medium text-gray-900">
                          {previewReport.category === 'water'
                            ? 'Wodociągi'
                            : previewReport.category === 'sewer'
                              ? 'Kanalizacja'
                              : '-'}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Zakończone</div>
                        <div className="text-base font-medium text-gray-900">{previewReport.is_finished ? 'Tak' : 'Nie'}</div>
                      </div>
                    </div>

                    {!previewReport.category && (
                      <div className="mb-6 border border-dashed border-gray-300 rounded-md p-4 bg-gray-50">
                        <div className="text-sm text-gray-700 font-medium mb-3">
                          Raport nie ma przypisanej kategorii — wybierz i zapisz:
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                          <select
                            value={categoryToAssign}
                            onChange={(e) => setCategoryToAssign(e.target.value as 'water' | 'sewer' | '')}
                            className="w-full sm:w-64 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Wybierz kategorię</option>
                            <option value="water">Wodociągi</option>
                            <option value="sewer">Kanalizacja</option>
                          </select>
                          <button
                            onClick={assignCategory}
                            disabled={assigningCategory}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                          >
                            {assigningCategory ? 'Zapisywanie…' : 'Przypisz kategorię'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Obiekt infrastruktury */}
                    {previewReport.infrastructure_object && (
                      <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Obiekt infrastruktury</h2>
                        <div className="bg-blue-50 p-4 rounded-md">
                          <div className="space-y-2">
                            <div className="text-sm text-gray-500">Nazwa obiektu</div>
                            <div className="text-base font-medium text-gray-900">{previewReport.infrastructure_object.name}</div>
                            {previewReport.infrastructure_object.location && (
                              <>
                                <div className="text-sm text-gray-500">Miejscowość</div>
                                <div className="text-base text-gray-900">{previewReport.infrastructure_object.location}</div>
                              </>
                            )}
                            {previewReport.infrastructure_object.street_address && (
                              <>
                                <div className="text-sm text-gray-500">Adres</div>
                                <div className="text-base text-gray-900">{previewReport.infrastructure_object.street_address}</div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Adres interwencji */}
                    {!previewReport.infrastructure_object && (previewReport.location || previewReport.street_address) && (
                      <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Adres interwencji</h2>
                        <div className="space-y-1">
                          <div className="text-sm text-gray-500">Miejscowość</div>
                          <div className="text-base text-gray-900">{previewReport.location || '-'}</div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-sm text-gray-500">Ulica/nr działki</div>
                          <div className="text-base text-gray-900">{previewReport.street_address || '-'}</div>
                        </div>
                      </div>
                    )}

                    {/* Mapa lokalizacji */}
                    {previewReport.latitude && previewReport.longitude && (
                      <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Lokalizacja na mapie</h2>
                        <div className="h-64 w-full border border-gray-300 rounded-md overflow-hidden">
                          <MapLibrePreviewMap
                            latitude={parseFloat(previewReport.latitude)}
                            longitude={parseFloat(previewReport.longitude)}
                            reportId={previewReport.id}
                            location={previewReport.location || ''}
                            streetAddress={previewReport.street_address || ''}
                          />
                        </div>
                      </div>
                    )}

                    {/* Zgłoszenie (tylko dla awarii) */}
                    {previewReport.work_type === 'awaria' && (
                      <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Szczegóły zgłoszenia</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                          <div className="space-y-1">
                            <div className="text-sm text-gray-500">Kto zgłosił</div>
                            <div className="text-base text-gray-900">
                              {previewReport.reported_by === 'system' ? 'System' : 'Osoba fizyczna / Referat'}
                            </div>
                          </div>
                          {previewReport.reported_by === 'osoba' && (
                            <>
                              <div className="space-y-1">
                                <div className="text-sm text-gray-500">Imię i nazwisko</div>
                                <div className="text-base text-gray-900">{previewReport.reporter_name || '-'}</div>
                              </div>
                              <div className="space-y-1">
                                <div className="text-sm text-gray-500">Telefon</div>
                                <div className="text-base text-gray-900">{previewReport.reporter_phone || '-'}</div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Opis prac */}
                    {previewReport.description && (
                      <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Opis prac</h2>
                        <div className="whitespace-pre-wrap text-gray-900">{previewReport.description}</div>
                      </div>
                    )}

                    {/* Materiały */}
                    {previewReport.materials && (
                      <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Materiały / usługi</h2>
                        <div className="whitespace-pre-wrap text-gray-900">{previewReport.materials}</div>
                      </div>
                    )}

                    {/* Zalecenia */}
                    {previewReport.recommendations && (
                      <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Zalecenia</h2>
                        <div className="whitespace-pre-wrap text-gray-900">{previewReport.recommendations}</div>
                      </div>
                    )}

                    {/* Multimedia */}
                    {!!previewReport.images?.length && (
                      <div className="mb-2">
                        <h2 className="text-lg font-semibold text-gray-900 mb-3">Załączone zdjęcia</h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {previewReport.images!.map(img => (
                            <a
                              key={img.id}
                              href={`/${img.file_path}`}
                              target="_blank"
                              rel="noreferrer"
                              className="block border rounded-md overflow-hidden hover:shadow"
                              title={img.original_filename}
                            >
                              {/* Miniatura */}
                              <img
                                src={`/${img.file_path}`}
                                alt={img.original_filename}
                                className="w-full h-40 object-cover"
                              />
                              <div className="px-2 py-1 text-xs text-gray-700 truncate">
                                {img.original_filename}
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-4 sm:px-6 py-4 border-t shrink-0">
              <button onClick={closePreview} className="min-h-[44px] px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md">
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal MULTIMEDIÓW */}
      {mediaModalOpen && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="bg-white shadow-2xl w-full sm:max-w-4xl flex flex-col max-h-full sm:max-h-[90vh] sm:rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b shrink-0">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                Multimedia - Raport {mediaModalReport?.id}
              </h3>
              <button onClick={closeMediaModal} className="p-1 -mr-1 text-gray-400 hover:text-gray-600 shrink-0" aria-label="Zamknij">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
              {mediaModalLoading && (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Ładowanie multimediów…</span>
                </div>
              )}

              {!mediaModalLoading && mediaModalReport && (
                <div>
                  {mediaModalReport.images && mediaModalReport.images.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {mediaModalReport.images.map(img => (
                        <div key={img.id} className="border rounded-md overflow-hidden hover:shadow-lg transition-shadow">
                          <a
                            href={`/${img.file_path}`}
                            target="_blank"
                            rel="noreferrer"
                            className="block"
                          >
                            <img
                              src={`/${img.file_path}`}
                              alt={img.original_filename}
                              className="w-full h-48 object-cover hover:scale-105 transition-transform"
                            />
                          </a>
                          <div className="p-2">
                            <div className="text-xs text-gray-700 truncate font-medium">
                              {img.original_filename}
                            </div>
                            <div className="text-xs text-gray-500">
                              {(img.file_size / 1024).toFixed(1)} KB
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16 text-gray-500">
                      Brak multimediów dla tego raportu
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-4 sm:px-6 py-4 border-t shrink-0">
              <button onClick={closeMediaModal} className="min-h-[44px] px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md">
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
